import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Table2,
  Users,
  LineChart,
  Scale,
  Repeat,
  GitCompare,
  LogOut,
  CircleDot,
  Landmark,
  Building2,
  Bell,
  AlertTriangle,
  ScrollText,
  Trash2,
  UserCog,
  Palette,
  CalendarClock,
  NotebookPen,
  Network,
  Menu,
  X,
  CirclePause,
  AlarmClock,
} from "lucide-react";
import { api } from "@/lib/api";
import { Notices } from "@/components/notice";
import { useNotesDueCount, useOpenRiskEvents, useRiskChecksHealth } from "@/lib/queries";
import type { AuthUser } from "@match/shared";

/**
 * NOTE E CALENDARIO IN CIMA, fuori da ogni sezione: è la pagina che si apre
 * ogni giorno, e anche quella d'arrivo (`/` porta qui, App.tsx). Ha preso il
 * posto della Home, una Panoramica rimasta segnaposto. Il numero accanto sono
 * i promemoria scaduti e non fatti, e sparisce se è zero.
 */
const NOTE_ITEM = { to: "/note", label: "Note e calendario", icon: NotebookPen, badge: "notes" as const } as const;

/** I Clienti, subito sotto Note e calendario e fuori dalle sezioni: è da lì che si parte per ogni conto, ciclo e fase. */
const CLIENTI_ITEM = { to: "/clienti", label: "Clienti", icon: Users } as const;

/*
 * L'ORDINE SEGUE COME SI LAVORA, non l'ordine in cui le pagine sono nate.
 *
 * Prima l'operazione del giorno, poi i cicli (l'unità economica), le fasi
 * (l'operatività che li attraversa), il confronto e gli eventi. I clienti
 * stanno a sé, sotto Note e calendario; le anagrafiche che restano — prop firm, broker (coi server),
 * firm, eliminati — vengono dopo: si consultano, non è lì che si lavora ogni
 * giorno.
 */
const NAV = [
  { group: "Operatività", items: [
    { to: "/operazione", label: "Operazione", icon: LineChart },
    // Le aperture che il gestionale fa da solo all'ora scelta (26/10).
    { to: "/operazioni-programmate", label: "Operazioni programmate", icon: AlarmClock },
    { to: "/cicli", label: "Cicli operativi", icon: Repeat },
    { to: "/fasi", label: "Fasi", icon: Scale },
    // Le Fasi ferme del tenant, filtrabili per causa: restano anche in «Fasi».
    { to: "/fermi", label: "Fermi", icon: CirclePause },
    { to: "/confronto", label: "Confronto", icon: GitCompare },
    // Gli eventi APERTI: il numero accanto e' quanti sono, e sparisce se e' zero.
    { to: "/eventi", label: "Eventi", icon: AlertTriangle, badge: "risk" as const },
    { to: "/calendario-economico", label: "Calendario economico", icon: CalendarClock },
  ]},
  { group: "Anagrafiche", items: [
    { to: "/prop-firm", label: "Prop firm", icon: Building2 },
    { to: "/broker", label: "Broker", icon: Landmark },
    { to: "/eliminati", label: "Eliminati", icon: Trash2 },
  ]},
  // Solo ADMIN: la rotta lo rifiuta comunque agli altri ruoli.
  { group: "Tenant", adminOnly: true, items: [
    { to: "/utenti", label: "Utenti", icon: UserCog },
    { to: "/impostazioni/notifiche", label: "Notifiche", icon: Bell },
    { to: "/impostazioni/branding", label: "Branding", icon: Palette },
    { to: "/audit", label: "Audit", icon: ScrollText },
  ]},
  // Solo super admin di piattaforma: la rotta non esiste per gli altri (App.tsx), e il backend rifiuta comunque.
  { group: "Piattaforma", platformOnly: true, items: [
    { to: "/platform", label: "Tenant", icon: Network },
  ]},
] as const;

/**
 * LA BARRA DEL CLIENTE, e non è quella dell'operatore con meno voci.
 *
 * Un cliente non deve navigare il gestionale: deve vedere i suoi soldi. Fino a
 * qui vedeva Conti, Clienti, Server, Prop firm, Operazione, Fasi ed Eventi —
 * **sette voci su cui poteva solo prendere un 403**, perché il backend è una
 * allowlist di quattro rotte (`lib/client-scope.ts`).
 *
 * **Un menu di porte chiuse non è un menu**, ed è peggio di uno corto: la prima
 * volta che ci prova crede di aver rotto qualcosa, o capisce che ci sono pagine
 * che gli nascondiamo — l'impressione da non dare a chi ci affida dei soldi.
 *
 * Le voci sono quattro perché **quattro sono le rotte che il backend gli
 * apre**, e sono in quest'ordine perché è l'ordine delle domande che si fa:
 * quanto c'è sui miei conti, come stanno andando i cicli, che cosa è passato
 * dal registro, come sono andati i periodi.
 */
const NAV_CLIENT = [
  { group: "Il mio conto", items: [
    { to: "/", label: "I miei conti", icon: Table2 },
    { to: "/cicli", label: "I miei cicli", icon: Repeat },
    { to: "/movimenti", label: "Movimenti", icon: Scale },
    { to: "/confronto", label: "Confronto", icon: GitCompare },
  ]},
] as const;

/**
 * Quanti eventi sono aperti ADESSO, accanto alla voce di menu.
 *
 * **Se sono zero non c'e' niente**, e non uno zero: un contatore a zero
 * sempre acceso e' rumore, e dopo due giorni non lo si vede piu'. Il numero
 * compare solo quando c'e' qualcosa da guardare, e il colore dice se e' grave.
 *
 * Il conteggio arriva dalla stessa query della pagina: aprirla non fa una
 * seconda chiamata.
 */
function OpenEventsBadge({ enabled }: { enabled: boolean }) {
  // Un CLIENT non raggiunge quella rotta (403): non la si chiama nemmeno, o
  // sarebbe un rifiuto ogni trenta secondi per tutta la sua sessione.
  const q = useOpenRiskEvents({}, enabled);
  // Solo critici e avvisi: le notifiche (una coppia chiusa) non sono qualcosa da guardare.
  const n = (q.data?.counts.critical ?? 0) + (q.data?.counts.warning ?? 0);
  if (!n) return null;
  const grave = (q.data?.counts.critical ?? 0) > 0;
  return (
    <span
      className={`ml-auto rounded px-1.5 py-0.5 text-2xs font-medium ${grave ? "bg-loss/15 text-loss" : "bg-warn/15 text-warn"}`}
      title={grave ? `${q.data!.counts.critical} critici` : "avvisi aperti"}
    >
      {n}
    </span>
  );
}

/**
 * I PROMEMORIA SCADUTI E NON FATTI, accanto a «Note e calendario». Come il
 * contatore degli eventi: a zero non c'e', e quando c'e' e' rosso.
 */
function DueNotesBadge({ enabled }: { enabled: boolean }) {
  const q = useNotesDueCount(enabled);
  const n = q.data?.due ?? 0;
  if (!n) return null;
  return (
    <span className="ml-auto rounded bg-loss/15 px-1.5 py-0.5 text-2xs font-medium text-loss" title={n === 1 ? "un promemoria scaduto" : `${n} promemoria scaduti`}>
      {n}
    </span>
  );
}

/**
 * **LE GUARDIE SONO SPENTE**, e allora lo si legge da ogni schermata.
 *
 * `RISK_CHECK_INTERVAL_MS=0` non ferma una guardia: le ferma TUTTE — gamba
 * sparita, DD giornaliero, conti disconnessi, proxy, riconciliazione,
 * consuntivi in sospeso. In produzione e' stato zero per giorni, e l'unico
 * posto dove si vedeva era un file sulla VPS.
 *
 * **Compare solo quando c'e' un problema**, come il contatore degli eventi: una
 * riga verde «guardie attive» sempre accesa e' rumore, e dopo due giorni non la
 * si vede piu'. E copre due casi, perche' dalla configurazione si distingue
 * solo il primo:
 *
 *   - **spente**: l'intervallo e' zero;
 *   - **ferme**: l'intervallo c'e' ma il giro non arriva in fondo da un pezzo,
 *     che dal di fuori e' la stessa cosa.
 */
function GuardieSpente({ enabled }: { enabled: boolean }) {
  const q = useRiskChecksHealth(enabled);
  const r = q.data?.riskChecks;
  if (!r || (r.enabled && !r.stale)) return null;
  const minuti = r.ageMs === null ? null : Math.round(r.ageMs / 60_000);
  return (
    <div role="alert" className="border-b border-loss/30 bg-loss/10 px-4 py-2 text-sm text-loss sm:px-6">
      <span className="font-medium">
        {r.enabled ? "Controlli di rischio FERMI" : "Controlli di rischio DISATTIVATI"}
      </span>{" "}
      {r.enabled
        ? `— l'ultimo giro è ${minuti === null ? "sconosciuto" : `di ${minuti} minuti fa`}, l'intervallo è ${Math.round(r.intervalMs / 1000)} secondi.`
        : "— nessuna guardia sta girando: gamba scoperta, DD giornaliero, conti disconnessi e proxy non vengono controllati."}{" "}
      <span className="text-loss/80">Si riaccende con RISK_CHECK_INTERVAL_MS nel .env del server.</span>
    </div>
  );
}

const ROLE_LABEL: Record<AuthUser["role"], string> = {
  ADMIN: "Amministratore",
  OPERATORE: "Operatore",
  CONTABILE: "Contabile",
  VIEWER: "Sola lettura",
  CLIENT: "Cliente",
};

export function Layout({
  user,
  tenantName,
  logoUrl,
  onSignedOut,
}: {
  user: AuthUser;
  tenantName: string;
  /** Il logo del marchio del tenant (`TenantBranding.logoDarkUrl`), o quello della bozza in anteprima. Null = nessun logo dichiarato. */
  logoUrl?: string | null;
  onSignedOut: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  /*
   * DA CELLULARE LA BARRA SI RICHIUDE, per tutti i ruoli (02/11): sotto `md`
   * esce di lato e il contenuto prende tutta la larghezza; da `md` in su e'
   * quella di sempre. Le voci restano quelle del ruolo: il CLIENT vede le sue.
   */
  const [menuAperto, setMenuAperto] = useState(false);
  const chiudiMenu = () => setMenuAperto(false);
  // Ogni navigazione chiude il cassetto, anche verso la pagina in cui si e' gia'.
  useEffect(() => setMenuAperto(false), [location.key]);

  const signOut = async () => {
    await api.logout();
    navigate("/", { replace: true });
    onSignedOut();
  };

  return (
    <div className="flex min-h-screen bg-page">
      {menuAperto && (
        <div className="fixed inset-0 z-30 bg-black/40 md:hidden" onClick={chiudiMenu} aria-hidden />
      )}
      <aside
        className={[
          "flex w-56 shrink-0 flex-col border-r border-line bg-sidebar",
          // Da `md` in su niente posizione, livello ne' trasformazione: la barra di sempre.
          `fixed inset-y-0 left-0 z-40 transition-transform md:static md:z-auto md:transform-none ${menuAperto ? "translate-x-0" : "-translate-x-full"}`,
        ].join(" ")}
      >
        <div className="flex h-14 items-center gap-2 border-b border-line px-4">
          {/*
           * Col logo del tenant, se il marchio ne ha uno (`--strong`: solo qui
           * e nel nome). Senza, il marchio resta le due facce accostate — la
           * stessa idea del prodotto, e quello che ogni tenant senza un logo
           * suo vede oggi.
           */}
          {logoUrl ? (
            <img src={logoUrl} alt="" className="h-6 w-6 shrink-0 object-contain" aria-hidden />
          ) : (
            <span className="flex shrink-0" aria-hidden>
              <CircleDot className="h-4 w-4 text-prop" strokeWidth={2.5} />
              <CircleDot className="-ml-1.5 h-4 w-4 text-broker" strokeWidth={2.5} />
            </span>
          )}
          <span className="truncate text-sm font-semibold tracking-tight text-strong">{tenantName}</span>
          <button onClick={chiudiMenu} aria-label="Chiudi menu" className="ml-auto text-muted hover:text-ink md:hidden">
            <X className="h-5 w-5" strokeWidth={1.75} />
          </button>
        </div>

        <nav className="flex-1 space-y-5 overflow-y-auto px-2 py-4">
          {/* Note e calendario e Clienti stanno fuori dalle sezioni: la barra del CLIENT ha già la sua "/" dentro "Il mio conto". */}
          {user.role !== "CLIENT" && (
            <div className="space-y-0.5">
              {[NOTE_ITEM, CLIENTI_ITEM].map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    [
                      "flex items-center gap-2.5 rounded border-l-2 px-2 py-1.5 text-sm transition-colors",
                      isActive
                        ? "border-accent bg-raised font-medium text-accent"
                        : "border-transparent text-muted hover:bg-raised/60 hover:text-ink",
                    ].join(" ")
                  }
                >
                  <item.icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                  {item.label}
                  {"badge" in item && <DueNotesBadge enabled />}
                </NavLink>
              ))}
            </div>
          )}
          {(user.role === "CLIENT" ? NAV_CLIENT : NAV)
            .filter((section) => !("adminOnly" in section) || user.role === "ADMIN")
            .filter((section) => !("platformOnly" in section) || user.isPlatformAdmin)
            .map((section) => (
            <div key={section.group}>
              <p className="px-2 pb-1.5 text-2xs font-medium text-faint">{section.group}</p>
              {section.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/"}
                    onClick={chiudiMenu}
                    className={({ isActive }) =>
                      [
                        "flex items-center gap-2.5 rounded border-l-2 px-2 py-1.5 text-sm transition-colors",
                        isActive
                          ? "border-accent bg-raised font-medium text-accent"
                          : "border-transparent text-muted hover:bg-raised/60 hover:text-ink",
                      ].join(" ")
                    }
                  >
                    <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                    {item.label}
                    {"badge" in item && item.badge === "risk" && <OpenEventsBadge enabled={user.role !== "CLIENT"} />}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="border-t border-line p-3">
          <p className="truncate text-sm text-ink">{user.fullName ?? user.email}</p>
          <p className="text-2xs text-faint">{ROLE_LABEL[user.role]}</p>
          <button
            onClick={signOut}
            className="mt-2 flex items-center gap-1.5 text-xs text-muted hover:text-ink"
          >
            <LogOut className="h-3.5 w-3.5" strokeWidth={1.75} />
            Esci
          </button>
        </div>
      </aside>

      <main className="min-w-0 flex-1 overflow-x-hidden">
        <div className="flex h-14 items-center gap-3 border-b border-line bg-sidebar px-4 md:hidden">
          <button onClick={() => setMenuAperto(true)} aria-label="Apri menu" aria-expanded={menuAperto} className="text-muted hover:text-ink">
            <Menu className="h-5 w-5" strokeWidth={1.75} />
          </button>
          <span className="truncate text-sm font-semibold tracking-tight text-strong">{tenantName}</span>
        </div>
        {/* Se le guardie sono spente si vede da OGNI pagina, non da una. */}
        <GuardieSpente enabled={user.role !== "CLIENT"} />
        {/* Le pagine leggono l'utente (e quindi i permessi) da qui. */}
        <Outlet context={user} />
      </main>
      <Notices />
    </div>
  );
}

/** Intestazione di pagina. Un solo posto per titolo, sottotitolo e azioni. */
export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  return (
    <header className="flex items-start justify-between gap-4 border-b border-line px-6 py-4">
      <div>
        <h1 className="text-lg font-semibold tracking-tight">{title}</h1>
        {subtitle && <p className="mt-0.5 text-sm text-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex shrink-0 gap-2">{actions}</div>}
    </header>
  );
}

/**
 * Stato vuoto. Non è un messaggio di scuse: dice cosa fare per riempirlo.
 */
export function EmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="mx-6 my-6 rounded-card border border-dashed border-line px-6 py-14 text-center">
      <p className="text-sm font-medium text-ink">{title}</p>
      <p className="mx-auto mt-1 max-w-sm text-sm text-muted">{hint}</p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/** Badge del ruolo conto. Prop e broker mantengono il loro colore ovunque. */
export function RoleBadge({ role }: { role: "PROP" | "BROKER" | "NONE" }) {
  if (role === "NONE") {
    return (
      <span className="rounded border border-line px-1.5 py-0.5 text-2xs text-faint">
        senza ruolo
      </span>
    );
  }
  const isProp = role === "PROP";
  return (
    <span
      className={[
        "rounded px-1.5 py-0.5 text-2xs font-medium",
        isProp ? "bg-prop/15 text-prop" : "bg-broker/15 text-broker",
      ].join(" ")}
    >
      {isProp ? "Prop" : "Broker"}
    </span>
  );
}
